// pages/reports/AttendanceByDepartment.js
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spinner, Table } from 'react-bootstrap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import useReportData from '../../hooks/useReportData';
import { getUser } from '../../utils/auth';

// Departments arrive pre-sorted by company then department name. Grouping
// consecutive same-company rows gives the rowSpan sizes for the grouped
// summary table — the row-oriented transpose of the same approach the
// Course Completion Matrix uses for its two-row column header.
function groupByCompany(departments) {
  const groups = [];
  for (const dept of departments) {
    const last = groups[groups.length - 1];
    if (last && last.companyId === dept.company_id) {
      last.departments.push(dept);
    } else {
      groups.push({ companyId: dept.company_id, companyName: dept.company_name, departments: [dept] });
    }
  }
  return groups;
}

function AttendanceByDepartment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const departmentId = searchParams.get('department_id') || '';
  const [filters, setFilters] = useState(null);
  const loggedInUser = getUser();

  const effectiveFilters = filters ? { ...filters, departmentId } : null;
  const { data, loading, error, exportUrl } = useReportData('reports/get_attendance_by_department.php', effectiveFilters);

  const handleDrillDown = (id) => setSearchParams({ department_id: id });
  const handleBackToSummary = () => setSearchParams({});

  const companyGroups = data?.mode === 'summary' ? groupByCompany(data.departments) : [];
  const showCompanyColumn = companyGroups.length > 1;

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center no-print">
        <h2>Attendance by Department</h2>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
          {exportUrl && (
            <Button as="a" href={exportUrl} variant="outline-primary">
              Export to CSV
            </Button>
          )}
        </div>
      </div>

      <ReportFilterBar value={filters} onChange={setFilters} showDepartmentFilter={false} />

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <Spinner animation="border" size="sm" />}

      {data && !loading && data.mode === 'detail' && (
        <>
          <h5 className="text-muted">
            {data.department?.company_name ? `${data.department.company_name} — ` : ''}
            {data.department?.name}
          </h5>
          <h6 className="text-muted">{data.start} to {data.end}</h6>
          {loggedInUser?.role !== 'departmentadmin' && (
            <Button variant="link" className="p-0 mb-2 no-print" onClick={handleBackToSummary}>
              &laquo; Back to Summary
            </Button>
          )}
          <Table striped bordered hover size="sm">
            <thead>
              <tr><th>Employee Name</th><th>Attended Courses</th></tr>
            </thead>
            <tbody>
              {data.employees.length > 0 ? data.employees.map(emp => (
                <tr key={emp.id}>
                  <td>{emp.name}</td>
                  <td>{emp.attendedCourseCount}</td>
                </tr>
              )) : (
                <tr><td colSpan="2" className="text-center">No employees in this department</td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      {data && !loading && data.mode === 'summary' && (
        <>
          <h5 className="text-muted">{data.start} to {data.end}</h5>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                {showCompanyColumn && <th>Company</th>}
                <th>Department</th>
                <th>Attendance Count</th>
                <th className="no-print">Action</th>
              </tr>
            </thead>
            <tbody>
              {companyGroups.length > 0 ? companyGroups.map(group => (
                group.departments.map((dept, i) => (
                  <tr key={dept.id}>
                    {showCompanyColumn && i === 0 && (
                      <td rowSpan={group.departments.length}>{group.companyName}</td>
                    )}
                    <td>{dept.name}</td>
                    <td>{dept.attendanceCount}</td>
                    <td className="no-print">
                      <Button size="sm" variant="outline-primary" onClick={() => handleDrillDown(dept.id)}>
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))
              )) : (
                <tr><td colSpan="4" className="text-center">No departments in scope</td></tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
}

export default AttendanceByDepartment;
